import React from 'react';
import Page from '../components/Page';
import { Box, Button, Grid, Typography } from '@mui/material';
import { AppColors, typographyClamps } from '../utils/utils';
import { HtmlRenderer } from '../components/HtmlRenderer';
import { HowItWorksSectionView } from '../components/HowItWorksSectionView';
import { useNavigate } from 'react-router-dom';

interface FooterSection {
  imagePath: string;
  title: string;
  description: string;
  showGetStartedButton?: boolean;
}

const HowItWorks: React.FC = () => {
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px
  // const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md')); // if screen <= 'ms' for tablet and mobile device

  const xMargin = { xs: 0, sm: 0, md: '8%', lg: '10%' };

  const navigate = useNavigate();

  const handleGetStartedClick = () => {
    navigate('/pricing');
  };

  return (
    <>
      <Page title={'Pricing'} sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          component={'img'}
          src={'/static/home_page/img_light_blue_circle_1.png'}
          sx={{
            transform: `rotate(270deg)`,
            position: 'absolute',
            // left: 0,
            left: { xs: '-125%', sm: '-60%', md: '-50%', lg: '-20%' },
            top: { xs: '5%', sm: '20%' },
          }}
        />

        <Box
          component={'img'}
          src={
            '/static/how_it_works_page/img_how_it_works_background_small.png'
          }
          sx={{
            position: 'absolute',
            // transform: `rotate(90deg)`,
            // right: 0,
            height: '50%',
            // right: { xs: '-125%', sm: '-70%', md: '-50%', lg: '-20%' },
            right: 0,
            bottom: '10%',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            p: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
              px: 3,
              background: `url('/static/how_it_works_page/img_how_it_works_banner.png')`,
              backgroundPosition: 'center',
              backgroundSize: { xs: '150% 100%', sm: '100% 100%' },
              // backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              color: 'white',
              height: { xs: '30vh', sm: '70vh' },
              position: 'relative',
              borderRadius: 8,
            }}
          >
            <Typography
              sx={{
                fontSize: 'clamp(1.4rem, 5vw, 3.5rem)',
                color: 'gold',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              See How it All Works
            </Typography>

            <Box
              width={'100%'}
              display={'flex'}
              justifyContent={'end'}
              alignItems={'flex-end'}
            >
              <Box
                component={'img'}
                src='/static/images/qr_buddy_log_white.png'
                height={'30%'}
              />
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: '16px',
              mx: { xs: 0, sm: '8%', md: '15%', xl: '25%' },
              my: { xs: 4, sm: 8 },
              position: 'relative',
            }}
          >
            <Box
              component={'img'}
              src='/static/images/img_rob_profile_pic.png'
              sx={{
                position: 'absolute',
                bottom: 0,
                right: { xs: 0, sm: '-10%', md: '-8%' },
                height: { xs: '80%', sm: '130%' },
              }}
            />
            <Box
              sx={{
                background: AppColors.textGradient,
                borderRadius: '16px',
                padding: { xs: '16px 30% 16px 32px', sm: '16px 20% 16px 32px' },
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: typographyClamps.body2,
                    sm: typographyClamps.h6,
                  },
                  color: AppColors.whiteColor,
                  fontStyle: 'italic',
                }}
              >
                “We created QR Buddy to bridge the gap between customers and
                dealerships—combining human connection with AI intelligence to
                drive real, measurable growth.”
              </Typography>
              <Typography
                sx={{
                  fontSize: {
                    xs: typographyClamps.caption,
                    sm: typographyClamps.body2,
                  },
                  color: AppColors.blackColor,
                  fontStyle: 'italic',
                }}
              >
                — Rob Pickering, Founder of QR Buddy
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mx: xMargin,
            }}
          >
            <HowItWorksSectionView
              idx={0}
              label='Scan a Vehicle'
              title={''}
              subtitle=''
              features={[
                {
                  title: 'Enhanced Customer Engagement',
                  description:
                    'Each scan turns a parked car into a live digital experience with instant access to pricing, photos, and details.',
                },
                {
                  title: 'Real-Time Dealership Alerts',
                  description:
                    'Dealers get notified the moment a customer scans—making instant follow-up effortless.',
                },
                {
                  title: 'Modern, Interactive Experience',
                  description:
                    'Replaces outdated window shopping with a seamless, tech-driven connection between buyer and dealership.',
                },
              ]}
              footerText=''
              imagePath={'/static/how_it_works_page/img_how_it_works_1.png'}
            />

            <HowItWorksSectionView
              idx={1}
              label='Quick Sign On'
              title={''}
              subtitle=''
              features={[
                {
                  title: 'Quick Sign-in / Sign up',
                  description:
                    'Get customers connected instantly with one-tap sign-in — no delays, no setup friction, just immediate engagement.',
                },
                {
                  title: 'Web Interface – No Download Needed',
                  description:
                    'QR Buddy runs right in the browser, giving every shopper full access without requiring an app download or login hassle.',
                },
                {
                  title: 'Enticing Offers Drive More Sign-Ins',
                  description:
                    'Custom promotions and smart incentives boost interaction rates, motivating customers to sign in and stay connected.',
                },
              ]}
              footerText=''
              imagePath={'/static/how_it_works_page/img_how_it_works_2.png'}
            />
            <HowItWorksSectionView
              idx={2}
              label='Dealer Reporting'
              title={''}
              subtitle=''
              features={[
                {
                  title: 'Real-Time Dealer Dashboard',
                  description:
                    'Every lead is tracked instantly with live updates, disposition status, and performance insights—all visible in one centralized dealer view.',
                },
                {
                  title: 'AI Lead Distribution',
                  description:
                    'Leads are automatically routed to the right sales reps based on activity, responsiveness, and real-time performance metrics.',
                },
                {
                  title: 'Customer Behavior Tracking',
                  description:
                    'See which vehicles customers are scanning, how long they engage, and what captures their interest—giving dealers powerful data to close more sales.',
                },
              ]}
              footerText=''
              imagePath={'/static/how_it_works_page/img_how_it_works_3.png'}
            />

            <Grid
              container
              gap={4}
              sx={{
                my: 3,
                justifyContent: 'center',
                height: '100%',
              }}
            >
              {(
                [
                  {
                    imagePath:
                      '/static/how_it_works_page/img_how_it_works_automative.png',
                    title:
                      'More Than Automotive — QR Buddy Powers Every Dealership',
                    description:
                      'From cars to motorcycles, RVs, boats, and ATVs — QR Buddy connects customers to inventory and sales in seconds. One platform, every dealership.',
                    showGetStartedButton: true,
                  },
                  {
                    imagePath:
                      '/static/how_it_works_page/img_dealer_dashboard.png',
                    title: 'Dealer Dashboard',
                    description:
                      'Real-time visibility into leads, scans, and sales—track performance, manage reps, and stay in control from one powerful interface.<br /><br />Our exclusive leads, never resold. Track every scan, lead, and sale in real time—all managed from one powerful dashboard.',
                    showGetStartedButton: false,
                  },
                ] as FooterSection[]
              ).map((item) => (
                <Grid size={{ xs: 12, sm: 5.7 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: AppColors.lightBlueTransparentColor,
                      borderRadius: 8,
                      px: 4,
                      pt: 2,
                      pb: { xs: 2, sm: 0 },
                      height: { xs: 'auto', sm: '100%' },
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component={'img'}
                      src={item.imagePath}
                      sx={{
                        width: '100%',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: {
                          xs: typographyClamps.h6,
                          sm: typographyClamps.fontSize17,
                        },
                        fontWeight: 700,
                        mt: 4,
                        textAlign: 'center',
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: typographyClamps.fontSize15,
                        fontWeight: 600,
                        py: 4,
                      }}
                    >
                      <HtmlRenderer htmlString={item.description} />
                    </Typography>

                    {item.showGetStartedButton && (
                      <Button
                        variant='contained'
                        sx={{
                          ml: 2,
                          width: 'fit-content',
                          height: 42,
                          borderRadius: '12px',
                          border: 0,
                          color: 'white',
                          padding: '10px 32px',
                          backgroundColor: AppColors.azureColor,
                          fontWeight: 'bold',
                          textTransform: 'none',
                          fontSize: 'clamp(0.8rem, 2vw, 1.2rem)',
                        }}
                        onClick={handleGetStartedClick}
                      >
                        Get Started
                      </Button>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 6,
              }}
            >
              <Box
                display={'flex'}
                flexDirection={{ xs: 'column', sm: 'row' }}
                textAlign={'center'}
              >
                <Typography
                  component='span'
                  sx={{
                    fontSize: typographyClamps.fontSize17,
                    '& span': {
                      color: AppColors.vividBlueColor,
                      fontWeight: 600,
                    },
                  }}
                >
                  Where <span>Human Connection</span> Meets&nbsp;
                </Typography>
                <Typography
                  component={'span'}
                  sx={{
                    fontSize: typographyClamps.fontSize17,
                    background: AppColors.aiGradient,
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    fontWeight: 600,
                  }}
                >
                  Intelligent Automation
                </Typography>
              </Box>

              <Typography color='text.secondary' textAlign={'center'} mt={1}>
                QR Buddy unites your sales reps, AI agent, analytics, and
                instant lead tools—delivering faster responses, smarter
                insights, and real results.
              </Typography>

              <Box
                component={'img'}
                src='/static/how_it_works_page/img_how_it_works_footer.png'
                sx={{ width: { xs: '100%', sm: '50%' }, mt: 3 }}
              />
            </Box>
          </Box>
        </Box>
      </Page>
    </>
  );
};

export default HowItWorks;
